/**
 * Bulgarian Belot's UI module.
 *
 * What Belot says that French Belote does not: one ascending auction of six
 * contracts — the four suits, No Trumps, All Trumps — with contra and
 * recontra; declarations heard by kind after the first trick; and a score
 * kept in game points, the hand's count divided by ten. Two players play it
 * head to head, so nothing here assumes a partner.
 */
import type { ReactNode } from 'react'
import {
  BELOT_MODES,
  belotPoints,
  belotRanking,
  rankOf,
  suitOf,
  type BelotAnnouncement,
  type BelotContract,
  type BelotHandExtra,
  type BelotMode,
  type RankingContext,
  type Seat,
  type Suit,
} from '@cards/shared'
import type { BidLayoutProps, GameUi, LegalCall } from '../types'
import type { TableView } from '../../table/view'
import { Glyph } from '../../table/atoms'
import { BelotRankGuide } from './RankGuide'
import { CardWord, auctionCallsOf, auctionStripOf, handHint, readCalls, seatStatus, sortBySuit, winningSeat } from '../common'

const TRICKS = 8
const GLYPH: Record<string, Suit> = { '♠': 'S', '♥': 'H', '♦': 'D', '♣': 'C' }
const isRecord = (x: unknown): x is Record<string, unknown> => typeof x === 'object' && x !== null
const isSuit = (m: unknown): m is Suit => m === 'S' || m === 'H' || m === 'D' || m === 'C'

function contractOf(view: TableView): BelotContract | null {
  const c = view.hand?.contract
  return isRecord(c) && typeof c.taker === 'number' && typeof c.mode === 'string' ? (c as unknown as BelotContract) : null
}

function extraOf(view: TableView): Partial<BelotHandExtra> {
  return (view.hand?.extra ?? {}) as Partial<BelotHandExtra>
}

const contexts = new WeakMap<object, RankingContext>()
function rankingFor(view: TableView): RankingContext {
  const key = view.hand as object | null
  const hit = key ? contexts.get(key) : undefined
  if (hit) return hit
  const ctx = belotRanking(contractOf(view)?.mode ?? null)
  if (key) contexts.set(key, ctx)
  return ctx
}

/** `♠ ×2`, `NT`, `AT ×4` — the module's own label, drawn with glyphs. */
function LabelText({ label }: { label: string }): JSX.Element {
  const parts = label.split(/([♠♥♦♣])/)
  return (
    <span>
      {parts.map((p, i) => (GLYPH[p] ? <Glyph key={i} suit={GLYPH[p]} /> : <span key={i}>{p}</span>))}
    </span>
  )
}

function ModeText({ mode }: { mode: BelotMode }): JSX.Element {
  return isSuit(mode) ? <Glyph suit={mode} /> : <span>{mode}</span>
}

function callLabel(call: unknown): { label: ReactNode; pass: boolean } {
  if (!isRecord(call)) return { label: '?', pass: false }
  switch (call.type) {
    case 'pass': return { label: 'pass', pass: true }
    case 'bid': return { label: <ModeText mode={call.mode as BelotMode} />, pass: false }
    case 'contra': return { label: 'contra', pass: false }
    case 'recontra': return { label: 'recontra', pass: false }
    default: return { label: '?', pass: false }
  }
}

const KIND: Record<BelotAnnouncement['kind'], string> = {
  tierce: 'tierce', quarte: 'quarte', quint: 'quint', carre: 'carré',
}

const MODE_NAME: Record<string, string> = { NT: 'no trumps', AT: 'all trumps' }
/** What a screen reader says for a cell: the contract, not "Bid …" — the
 *  panel's own primary button is the one that bids. */
const CONTRACT_NAME: Record<string, string> = { C: 'clubs', D: 'diamonds', H: 'hearts', S: 'spades', ...MODE_NAME }

/** Six contracts in a row, lowest to highest, and the double beside them. */
function BelotBids({ calls, selected, onSelect }: BidLayoutProps): JSX.Element {
  const live = new Map<string, LegalCall>()
  for (const call of calls) live.set(call.key, call)
  const cell = (key: string, label: ReactNode) => {
    const call = live.get(key)
    const sel = selected?.key === key
    return (
      <button
        key={key}
        type="button"
        className={`cell${call ? '' : ' dead'}${sel ? ' sel' : ''}`}
        disabled={!call}
        onClick={() => onSelect(sel ? null : (call ?? null))}
        aria-pressed={sel}
        aria-label={key.startsWith('bid:') ? CONTRACT_NAME[key.slice(4)] : undefined}
      >
        {label}
      </button>
    )
  }
  const doubling = live.get('recontra') ?? live.get('contra') ?? null
  return (
    <div className="belot-bids">
      <div className="ladder" style={{ gridTemplateColumns: 'repeat(6, 1fr)' }}>
        {BELOT_MODES.map((mode) => cell(`bid:${mode}`, <ModeText mode={mode} />))}
      </div>
      {doubling ? (
        <div className="ladder-misere" style={{ gridTemplateColumns: '1fr' }}>
          {cell(doubling.key, doubling.label)}
        </div>
      ) : null}
    </div>
  )
}

export const belotUi: GameUi = {
  id: 'belot',
  name: 'Bulgarian Belot',
  rotation: 'anticlockwise',
  options: [
    {
      key: 'players',
      label: 'Players',
      choices: [{ label: '4, in pairs', value: 4 }, { label: '2, head to head', value: 2 }],
    },
  ],

  tricksPerHand: () => TRICKS,
  tricksNeeded: () => null,

  contractReading: (view) => {
    const hand = view.hand
    if (!hand) return null
    const contract = contractOf(view)
    if (contract) {
      return {
        node: (
          <>
            <LabelText label={contract.label} />
            <span className="who">{contract.taker === view.mySeat ? 'You' : view.name(contract.taker)}</span>
          </>
        ),
        caption: isSuit(contract.mode) ? 'trump' : MODE_NAME[contract.mode],
      }
    }
    const auction = hand.auction
    if (isRecord(auction) && isRecord(auction.high)) {
      const high = auction.high as { seat: Seat; mode: BelotMode }
      const mult = auction.mult === 4 ? ' ×4' : auction.mult === 2 ? ' ×2' : ''
      return {
        node: (
          <>
            <span><ModeText mode={high.mode} />{mult}</span>
            <span className="who">{high.seat === view.mySeat ? 'You' : view.name(high.seat)}</span>
          </>
        ),
        caption: 'bid to beat',
      }
    }
    return { node: <span className="faint">—</span>, caption: hand.phase === 'abandoned' ? 'passed out' : 'no bid yet' }
  },

  trackCaption: (view) => {
    const hand = view.hand
    if (!hand) return ''
    if (hand.phase === 'abandoned') return 'no tricks played'
    const mode = contractOf(view)?.mode ?? null
    const double = mode === 'NT' ? 2 : 1
    let us = 0
    let them = 0
    for (const t of hand.tricks) {
      if (t.winner === null || !view.match) continue
      const pts = (t.plays.reduce((n, p) => n + belotPoints(p.card, mode), 0) + (t.index === TRICKS - 1 ? 10 : 0)) * double
      if (view.match.table.teamOf[t.winner] === view.myTeam) us += pts
      else them += pts
    }
    if (us + them === 0) return `${TRICKS} tricks to play`
    return `${us} points, them ${them}`
  },

  seatStatus: (view, seat) => {
    const extra = extraOf(view)
    const parts: ReactNode[] = []
    if (contractOf(view)?.taker === seat) parts.push('bidder')
    if ((extra.belots ?? []).some((b) => b.seat === seat)) parts.push('belot')
    const declared = (extra.announcements ?? []).filter((d) => d.seat === seat)
    if (declared.length > 0 && (view.hand?.tricks.length ?? 0) <= 2) parts.push(declared.map((d) => KIND[d.kind]).join(' + '))
    return seatStatus(view, seat, parts)
  },

  winningSeat,

  callout: (view) => {
    const trick = view.trick
    const extra = extraOf(view)
    // Between trick 1 and trick 2 the felt says whose declarations stand.
    if (trick && trick.index === 1 && trick.plays.length === 0 && extra.announcements && extra.announcements.length > 0) {
      const counting = extra.announcements.filter((a) => a.counts)
      if (counting.length === 0) return <>Declarations cancel out</>
      const bySeat = new Map<Seat, BelotAnnouncement[]>()
      for (const a of counting) bySeat.set(a.seat, [...(bySeat.get(a.seat) ?? []), a])
      return (
        <>
          {[...bySeat.entries()].map(([seat, list], i) => (
            <span key={seat}>
              {i > 0 ? '; ' : ''}
              <b>{view.name(seat)}</b> scores {list.map((a) => KIND[a.kind]).join(' and ')}, {list.reduce((n, a) => n + a.points, 0)}
            </span>
          ))}
        </>
      )
    }
    if (!trick || trick.plays.length === 0) return null
    const leading = trick.winner ?? trick.winning
    const card = trick.plays.find((p) => p.seat === leading)?.card ?? null
    if (leading === null || card === null) return null
    const mode = contractOf(view)?.mode ?? null
    const points = trick.plays.reduce((n, p) => n + belotPoints(p.card, mode), 0) * (mode === 'NT' ? 2 : 1)
    const tail = points > 0 ? <>, {points} points</> : null
    return trick.winner !== null
      ? <><b>{view.name(leading)}</b> takes it with <CardWord card={card} />{tail}</>
      : <><b>{view.name(leading)}</b> has it with <CardWord card={card} />{tail}</>
  },

  handHint: (view) => {
    const trick = view.trick
    const mode = contractOf(view)?.mode ?? null
    if (view.isMyTurn && trick && trick.plays.length > 0 && trick.ledSuit && mode) {
      const legal = [...view.legalPlays.keys()]
      const restricted = legal.length < view.myCards.length
      if (restricted && isSuit(mode) && trick.ledSuit !== mode && legal.length > 0 && legal.every((c) => suitOf(c) === mode)) {
        const trumped = trick.plays.some((p) => suitOf(p.card) === mode)
        return <><b>Your turn,</b> you must {trumped ? 'overtrump' : 'trump'} <Glyph suit={mode} /></>
      }
      if (restricted && (mode === 'AT' || trick.ledSuit === mode)) {
        return <><b>Your turn,</b> follow <Glyph suit={trick.ledSuit} /> and go over if you can</>
      }
    }
    return handHint(view)
  },

  cardMarks: (view, card) => {
    const ctx = rankingFor(view)
    const trump = ctx.isTrump(card)
    const r = rankOf(card)
    const mode = contractOf(view)?.mode ?? null
    const trumpOrder = trump || mode === 'AT'
    return { trump, stamp: trumpOrder && (r === 11 || r === 9) ? (r === 11 ? '20' : '14') : null }
  },

  sortHand: (view, cards) => sortBySuit(cards, rankingFor(view)),

  handResult: (view) => {
    const hand = view.hand
    if (hand?.phase === 'abandoned') {
      return { headline: 'Thrown in', sentence: 'Nobody bid, so the cards go back and the deal moves on.' }
    }
    const result = hand?.result
    const contract = contractOf(view)
    if (!result || !contract || !view.match) return null
    const table = view.match.table
    const team = table.teamOf[contract.taker]
    const mine = team === view.myTeam
    const other = table.teams.find((t) => t.id !== team)!.id
    const who = mine ? (contract.taker === view.mySeat ? 'You' : `You and ${view.name(contract.taker)}`) : view.name(contract.taker)
    const first = hand!.tricks[0]?.winner
    const valat = hand!.tricks.length === TRICKS && first != null
      && hand!.tricks.every((t) => t.winner !== null && table.teamOf[t.winner] === table.teamOf[first])
    if (result.contractMade === null) {
      return { headline: 'Hanging', sentence: `An exact tie. ${who === 'You' ? 'Your' : `${who}'s`} points wait for whoever wins the next hand.` }
    }
    if (result.contractMade) {
      return {
        headline: valat ? 'Valat' : mine ? 'Made it' : 'They made it',
        sentence: `${who} scored ${result.deltas[team]} to ${result.deltas[other]}.`,
      }
    }
    return {
      headline: mine ? 'Inside' : 'Put them inside',
      sentence: `${who} fell short, and ${mine ? 'they take' : 'you take'} ${result.deltas[other]}.`,
    }
  },

  contractLabelNode: (label) => <LabelText label={label} />,

  auctionRule: 'Play runs to the right. Bid ♣ ♦ ♥ ♠, No Trumps or All Trumps, each higher than the last; the bidding ends when everyone else passes.',

  auctionStrip: (view) => {
    const auction = view.hand?.auction
    const high = isRecord(auction) && isRecord(auction.high) ? (auction.high.seat as Seat) : null
    return auctionStripOf(view, readCalls(auction), callLabel, high)
  },

  auctionCalls: (view) => {
    const auction = view.hand?.auction
    const high = isRecord(auction) && isRecord(auction.high) ? (auction.high.seat as Seat) : null
    return auctionCallsOf(view, readCalls(auction), callLabel, high)
  },

  isPass: (call: LegalCall) => isRecord(call.call) && call.call.type === 'pass',

  explainHeadline: (call) => <LabelText label={labelOf(call)} />,
  bidButtonLabel: (call) => <LabelText label={isRecord(call.call) && call.call.type === 'bid' ? `Bid ${labelOf(call)}` : call.label} />,

  BidLayout: BelotBids,

  decisionLayout: () => 'sheet',
  decisionHint: () => null,
  decisionWaiting: () => 'to choose',

  RankGuide: ({ view }) => <BelotRankGuide mode={contractOf(view)?.mode ?? null} short={view.seatCount === 2} />,
}

function labelOf(call: LegalCall): string {
  if (isRecord(call.call) && call.call.type === 'bid') {
    const mode = call.call.mode as BelotMode
    return isSuit(mode) ? call.label : mode === 'NT' ? 'No Trumps' : 'All Trumps'
  }
  return call.label
}
